import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bluetooth, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '@/components/common/PageContainer'
import { AppButton } from '@/components/rfid/AppButton'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { ScanCaptureHint } from '@/components/scanner/ScanCaptureHint'
import { BRAND, SCAN_HERO_SOFT } from '@/constants/brand'
import { useRawScanSubscription } from '@/hooks/useRawScanSubscription'
import {
  ensureMockConnected,
  isMockScannerEnabled,
} from '@/services/scannerService'
import { useRawScanStore } from '@/stores/rawScanStore'
import { isScannerSessionActive, useRfidSessionStore } from '@/stores/rfidSessionStore'

type TagRow = { tagId: string; hits: number }

export default function RawScanPage() {
  const navigate = useNavigate()
  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerPending = useRfidSessionStore((s) => s.scannerPending)
  const deviceConnected = useRfidSessionStore((s) => s.deviceConnected)
  const scannerType = useRfidSessionStore((s) => s.scannerType)

  const rawScans = useRawScanStore((s) => s.rawScans)
  const uniqueTags = useRawScanStore((s) => s.uniqueTags)
  const tagHitCount = useRawScanStore((s) => s.tagHitCount)
  const clear = useRawScanStore((s) => s.clear)

  const [search, setSearch] = useState('')
  const mock = isMockScannerEnabled()

  useEffect(() => {
    if (mock) ensureMockConnected()
  }, [mock])

  const sessionActive = isScannerSessionActive({ scannerConnected, scannerPending })
  const enabled = sessionActive || mock
  useRawScanSubscription(enabled)

  const rows = useMemo<TagRow[]>(() => {
    const query = search.trim().toLowerCase()
    const tags = query
      ? uniqueTags.filter((tagId) => tagId.toLowerCase().includes(query))
      : uniqueTags
    return tags.map((tagId) => ({ tagId, hits: tagHitCount[tagId] ?? 1 }))
  }, [uniqueTags, tagHitCount, search])

  const scannerLabel = deviceConnected?.rfid_device ?? scannerType ?? 'scanner'

  const onClear = () => {
    clear()
    setSearch('')
    toast.message('Raw scan pool cleared')
  }

  return (
    <PageContainer fluid className="!flex !h-full !min-h-0 !flex-col !px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title="Raw scan"
          subtitle="No document — capture all tags into a test pool"
          onBack={() => navigate('/')}
          right={<ScanCaptureHint armed={enabled} showStatus={false} />}
        />

        <div className="rfid-wizard-body !flex !min-h-0 !flex-1 !flex-col !gap-3 !overflow-hidden">
          <div
            className="flex shrink-0 items-center rounded-2xl px-4 py-5"
            style={{ backgroundColor: SCAN_HERO_SOFT }}
          >
            <div className="flex flex-1 flex-col items-center">
              <div className="text-4xl font-black tabular-nums leading-none text-white">
                {rawScans}
              </div>
              <div className="mt-1 text-[11px] font-bold text-white/60">Raw scans</div>
            </div>
            <div className="h-12 w-px bg-white/20" />
            <div className="flex flex-1 flex-col items-center">
              <div className="text-4xl font-black tabular-nums leading-none text-white">
                {uniqueTags.length}
              </div>
              <div className="mt-1 text-[11px] font-bold text-white/60">Unique tags</div>
            </div>
          </div>

          {!enabled ? (
            <button
              type="button"
              onClick={() => navigate('/scanner')}
              className="flex shrink-0 items-center justify-center gap-2.5 rounded-xl border-[1.5px] border-border bg-card px-3.5 py-3.5 text-[13px] font-bold text-foreground"
            >
              <Bluetooth className="h-[18px] w-[18px]" style={{ color: BRAND }} strokeWidth={2} />
              Scanner not connected — tap to connect
            </button>
          ) : (
            <p className="m-0 shrink-0 text-center text-xs font-semibold leading-[17px] text-muted-foreground">
              Press the trigger on {scannerLabel} to scan tags.
            </p>
          )}

          <div className="shrink-0">
            <input
              type="search"
              className="rfid-search font-mono"
              placeholder="Search scanned tags…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search scanned tags"
              data-no-wedge="true"
              disabled={uniqueTags.length === 0}
            />
          </div>

          <div className="flex shrink-0 items-center justify-between gap-2">
            <div className="text-[13px] font-black text-foreground">Unique tags</div>
            <button
              type="button"
              onClick={onClear}
              disabled={uniqueTags.length === 0}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
              Clear
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-[14px] border border-border bg-card">
            {uniqueTags.length === 0 ? (
              <p className="m-0 px-4 py-6 text-center text-xs font-semibold text-muted-foreground">
                No tags yet — press the trigger to scan
              </p>
            ) : rows.length === 0 ? (
              <p className="m-0 px-4 py-6 text-center text-xs font-semibold text-muted-foreground">
                No tags match “{search.trim()}”
              </p>
            ) : (
              <ul className="m-0 list-none p-0">
                {rows.map((row, index) => (
                  <li
                    key={row.tagId}
                    className="flex items-center gap-2.5 px-3.5 py-3"
                    style={{
                      borderTop: index === 0 ? undefined : '1px solid var(--border)',
                    }}
                  >
                    <span className="min-w-0 flex-1 break-all font-mono text-[11.5px] font-bold text-foreground">
                      {row.tagId}
                    </span>
                    {row.hits > 1 ? (
                      <span
                        className="shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-black"
                        style={{
                          color: BRAND,
                          backgroundColor: `${BRAND}18`,
                        }}
                      >
                        ×{row.hits}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!enabled ? (
            <div className="shrink-0">
              <AppButton block onClick={() => navigate('/scanner')}>
                Connect scanner
              </AppButton>
            </div>
          ) : null}
        </div>
      </div>
    </PageContainer>
  )
}
